import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Product } from '../business/product/product';
import { ContextService } from './context.service';

@Injectable({
  providedIn: 'root'
})
export class SortingService {
  public isFirstLoad = true;
  private sortedProducts: Product[];
  public sortedProductsSave: Product[];
  private isVitafraisCompany: boolean;
  public typeSort: any = null;
  public isNetPrice: boolean;
  public isGroupingEnable: boolean = true;

  constructor(private contextService: ContextService) {
  }

  NgOnInit() {
  }

  /**
   * Tri un tableau de produits en les regroupant par groupe (propriété défini dans le champ "productListGroupingFieldName" de la table Companies).
   * Prend en compte le tri personnalisé de l'utilisateur et place les produits en promotion en tête de liste.
   * @param products Tableau de produits à trier
   * @param groupingPropertyFieldName Nom de la propriété pour le regroupement
   * @param sort Paramètre de tri personnalisé
   * @returns Tableau de produits triés
   */
  public sortAndGroupProductsByGroupWithGroupName(products: Product[], groupingPropertyFieldName: string, sort?: DataTableSort): Product[] {
    if (products[0][groupingPropertyFieldName] !== undefined) {

      this.sortedProducts = [...products];
      this.isVitafraisCompany = this.contextService.fullContext.company?.code == "VITAFRAIS";
      if (this.typeSort != null)
        this.sortProducts(this.sortedProducts, this.typeSort, this.isNetPrice, this.isVitafraisCompany);
      this.sortAndGroupProducts(groupingPropertyFieldName, sort);

    }

    return [...this.sortedProducts];
  }

  /**
   * Tri un tableau de produits en les regroupant par groupe (propriété défini dans le champ "productListGroupingFieldName" de la table Companies).
   * Prend en compte le tri personnalisé de l'utilisateur et place les produits en promotion en tête de liste.
   * Les noms des groupes sont ajoutés dans des lignes d'en-tête
   * Les articles avec plusieurs groupements sont dupliqués pour apparaître dans chacun de leurs groupes.
   * @param products Tableau de produits à trier
   * @param groupingPropertyFieldName Nom de la propriété pour le regroupement
   * @param sort Paramètre de tri personnalisé
   * @returns Tableau de produits triés
   */
  public sortAndGroupProductsByGroup(products: Product[], groupingPropertyFieldName: string, sort?: DataTableSort): Product[] {
    if (!products || products.length === 0 || products[0][groupingPropertyFieldName] === undefined) {
      return [];
    }
    
    // Explosion des produits qui ont plusieurs groupements
    const explodedProducts = products.flatMap((p: Product) => {
      const groupement = p[groupingPropertyFieldName];
      if (Array.isArray(groupement)) {
        return groupement.map(g => ({ ...p, [groupingPropertyFieldName]: g }));
      }
      return p;
    });

    this.sortedProducts = [...explodedProducts];
    this.isVitafraisCompany = this.contextService.fullContext.company?.code == "VITAFRAIS";
    if (this.typeSort != null)
      this.sortProducts(this.sortedProducts, this.typeSort, this.isNetPrice, this.isVitafraisCompany);
    if (this.isGroupingEnable) {
      this.sortAndGroupProducts(groupingPropertyFieldName, sort);
      this.addGroupName(groupingPropertyFieldName);
    }

    return [...this.sortedProducts];
  }

  /**
   * Tri un tableau de produits par ordre alphabétique (propriété "label")
   * Prend en compte le tri personnalisé de l'utilisateur et place les produits en promotion en tête de liste
   * @param products Tableau de produits à trier
   * @param sort Paramètre de tri personnalisé
   * @returns Tableau de produits triés
   */
  public sortProductByProductLabel(products: Product[], sort?: DataTableSort): Product[] {
    this.sortedProducts = [...products.sort(
                              (a, b): number => {
                                // Tri personnalisé basé sur "sort" (si défini)
                                if (sort) {
                                  return this.sortData(sort, a, b);
                                }

                                // Tri basé sur la propriété "discount"
                                if (a.discount && !b.discount) {
                                  return -1; // "a" a une remise, donc il doit être placé avant "b"
                                }
                                if (!a.discount && b.discount) {
                                  return 1; // "b" a une remise, donc il doit être placé avant "a"
                                }

                                // Tri par ordre alphabétique
                                return a.label.localeCompare(b.label);
                              }),
                          ];

    if (this.typeSort != null)
            return this.sortProducts(this.sortedProducts, this.typeSort, this.isNetPrice, this.isVitafraisCompany);
    return [...this.sortedProducts];
  }

  /**
   * Tri un tableau de produits en les regroupant par groupe (propriété défini dans le champ "productListGroupingFieldName" de la table Companies).
   * Prend en compte le tri personnalisé de l'utilisateur et place les produits en promotion en tête de liste.
   * @param groupingPropertyFieldName Nom de la propriété pour le regroupement
   * @param sort Paramètre de tri personnalisé
   */
  private sortAndGroupProducts(groupingPropertyFieldName: string, sort?: DataTableSort): void {
    // Tri des produits
    this.sortedProducts = [...this.sortedProducts.sort(
                              (a, b): number => {
                                // Tri basé sur la propriété "discount"
                                // Seulement pour VITAFRAIS car chez PRONATURA les produits en promotion sont déjà des groupes "PROMO")
                                if (this.isVitafraisCompany) {
                                  if (a.discount && !b.discount) {
                                    return -1; // "a" a une remise, donc il doit être placé avant "b"
                                  }
                                  if (!a.discount && b.discount) {
                                    return 1; // "b" a une remise, donc il doit être placé avant "a"
                                  }
                                }
                                
                                // Tri basé sur le groupe
                                if (a[groupingPropertyFieldName] < b[groupingPropertyFieldName]) {
                                  return -1;
                                }
                                if (a[groupingPropertyFieldName] > b[groupingPropertyFieldName]) {
                                  return 1;
                                }

                                // Tri personnalisé basé sur "sort" (si défini)
                                if (sort) {
                                  return this.sortData(sort, a, b);
                                }

                                return null; // Les produits sont équivalents en termes de tri
                              }),
                          ];
    if (this.typeSort != null)
      this.sortProducts(this.sortedProducts, this.typeSort, this.isNetPrice, this.isVitafraisCompany);
  }


  /**
   * Ajout des noms des groupes de produits (propriété défini dans le champ "productListGroupingFieldName" de la table Companies).
   * @param groupingPropertyFieldName Nom de la propriété pour le regroupement
   */
  private addGroupName(groupingPropertyFieldName: string): void {
    const isArray = this.sortedProducts[0][groupingPropertyFieldName] instanceof Array;
    const sortedData = [...this.sortedProducts]
    this.sortedProducts = new Array<Product>();

    // Insertion du premier groupe en tête de liste
    let oldGroupName: string;
    if (this.isVitafraisCompany && sortedData[0].discount)
      oldGroupName = "Promotion"; // Ajout d'un groupe "Promotion" qui n'existe pas chez VITAFRAIS
    else
      oldGroupName = isArray ? sortedData[0][groupingPropertyFieldName][0] : sortedData[0][groupingPropertyFieldName];

    const firstGroup = new Product();

    // Vérification si les 5 premiers caractères du groupe est un nombre (ProNatura)
    if (!this.isVitafraisCompany && !isNaN(parseInt(oldGroupName.substring(0, 5)))) {
      // Si oui, on retire les 6 premiers caractères (pour l'affichage)
      firstGroup['groupName'] = oldGroupName.substring(6);
    }
    else {
      firstGroup['groupName'] = oldGroupName;
    }

    firstGroup['isGroup'] = true;
    this.sortedProducts.push(firstGroup);

    // Ajout des lignes (produits et groupes) à la liste de produits triés
    sortedData.forEach(element => {
      if ((this.isVitafraisCompany && !element.discount) || !this.isVitafraisCompany) {
        if (element[groupingPropertyFieldName] && (isArray ? element[groupingPropertyFieldName][0] : element[groupingPropertyFieldName]) !== oldGroupName) {
          oldGroupName = isArray ? element[groupingPropertyFieldName][0] : element[groupingPropertyFieldName];

          // Ajout d'une ligne groupe
          const newProduct = new Product();

          // Vérification si les 5 premiers caractères du groupe est un nombre (ProNatura)
          if (!this.isVitafraisCompany && !isNaN(parseInt(oldGroupName.substring(0, 5)))) {
            // Si oui, on retire les 6 premiers caractères (pour l'affichage)
            newProduct['groupName'] = oldGroupName.substring(6);
          }
          else {
            newProduct['groupName'] = oldGroupName;
          }

          newProduct['isGroup'] = true;
          this.sortedProducts.push(newProduct);

        }
      }

      // Ajout d'une ligne produit
      this.sortedProducts.push(element);
    });
  }

  /**
   * Tri personnalisé de deux produits basé sur "event"
   * @param event Type de tri souhaité
   * @param product1
   * @param product2
   */
  private sortData(event: DataTableSort, product1: Product, product2: Product): number {
    // Définition d'une fonction de tri
    const sortFunction = (a, b): number => {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    };

    // Tri décroissant
    if (event.sortDir === 'desc') {
      const tmp = product1;
      product1 = product2;
      product2 = tmp;
    }

    // Tri personnalisé pour la colonne poids
    if (event.column === 'weight') {
      return sortFunction(
        product1.weight && product1.weight.net ? parseFloat(product1.weight.net.replace(',', '.')) : null,
        product2.weight && product2.weight.net ? parseFloat(product2.weight.net.replace(',', '.')) : null
      );
    }

    // Tri personnalisé pour la colonne prix
    if (event.column === 'price') {
      return sortFunction(
        product1.price && product1.price[0] && product1.price[0].price && product1.price[0].price.gross
          ? parseFloat(product1.price[0].price.gross.replace(',', '.')) : null,
        product2.price && product2.price[0] && product2.price[0].price && product2.price[0].price.gross
          ? parseFloat(product2.price[0].price.gross.replace(',', '.')) : null
      );
    }

    // Tri général pour les colonnes de type string
    if (typeof product1[event.column] === 'string') {
      return sortFunction(
        product1[event.column] ? product1[event.column].toLowerCase() : null,
        product2[event.column] ? product2[event.column].toLowerCase() : null
      );
    }

    // Tri général pour les colonnes de type number
    return sortFunction(product1[event.column], product2[event.column]);
  }

  public convertStringToNumber(value: string): number {
    return parseFloat(value.replace(',', '.'));
  }

  sortLabelProducts(typeSort: any) {
    let selectedSortString: string = '';
    switch (typeSort?.value) {
      case 'priceAsc':
        selectedSortString = 'Prix croissant';
        break;
      case 'priceDesc':
        selectedSortString = 'Prix décroissant';
        break;
      case 'priceKgAsc':
        selectedSortString = 'Prix Kg/L croissant';
        break;
      case 'priceKgDesc':
        selectedSortString = 'Prix Kg/L décroissant';
        break;
      case 'markAsc':
        selectedSortString = 'Marque de A à Z';
        break;
      case 'markDesc':
        selectedSortString = 'Marque de Z à A';
        break;
      case 'designationAsc':
        selectedSortString = 'Désignation de A à Z';
        break;
      case 'designationDesc':
        selectedSortString = 'Désignation de Z à A';
        break;
      default:
        selectedSortString = 'Trier par...';
        break;
    }

    return selectedSortString;
  }

  public sortProducts(sortedProducts: Product[], typeSort: any, isNetPrice: boolean, isVitafrais: boolean): Product[] {
    this.typeSort = typeSort;
    this.isNetPrice = isNetPrice;
    this.sortedProducts = sortedProducts;
    if (this.isFirstLoad) {
      this.sortedProductsSave = JSON.parse(JSON.stringify(this.sortedProducts));
      this.isFirstLoad = false;
    }

    // On trie les produits par prix défini puis par prix non défini
    const definedPrices = this.sortedProducts.filter((product) => product.price?.length > 0);
    const undefinedPrices = this.sortedProducts.filter((product) => product.price?.length == 0);
    const definedMark = this.sortedProducts.filter((product) => product.brand !== '');
    const undefinedMark = this.sortedProducts.filter((product) => product.brand == '');

    switch (typeSort?.value) {
      case 'priceAsc':
        // On compare les prix par poids/litre en fonction du type de prix sélectionné : prix net ou prix brut
        // Tri Ascendant
        if (isVitafrais) {
          definedPrices.sort((a, b) => {
            let currentPriceA = isNetPrice
            ? this.convertStringToNumber(a.price.length>1 ? a.price[1]?.price?.net : a.price[0]?.price?.net)
            : this.convertStringToNumber(a.price.length>1 ? a.price[1]?.price?.gross : a.price[0]?.price?.gross);
            let currentPriceB = isNetPrice
            ? this.convertStringToNumber(b.price.length>1 ? b.price[1]?.price?.net : b.price[0]?.price?.net)
            : this.convertStringToNumber(b.price.length>1 ? b.price[1]?.price?.gross : b.price[0]?.price?.gross);

            return (currentPriceA) - (currentPriceB);
          });
        } else {
          definedPrices.sort((a, b) => {
            return this.convertStringToNumber(a.price[0]?.price?.gross) - this.convertStringToNumber(b.price[0]?.price?.gross);
          });
        }

        // On concatène les produits avec prix défini et les produits avec prix non défini
        this.sortedProducts = definedPrices.concat(undefinedPrices);
        break;
      case 'priceDesc':
        // On compare les prix par poids/litre en fonction du type de prix sélectionné : prix net ou prix brut
        // Pour Pronatura il n'y a pas de prix net
        // Tri Ascendant
        if (isVitafrais) {
          definedPrices.sort((a, b) => {
            let currentPriceA = isNetPrice
            ? this.convertStringToNumber(a.price.length>1 ? a.price[1]?.price?.net : a.price[0]?.price?.net)
            : this.convertStringToNumber(a.price.length>1 ? a.price[1]?.price?.gross : a.price[0]?.price?.gross);
            let currentPriceB = isNetPrice
            ? this.convertStringToNumber(b.price.length>1 ? b.price[1]?.price?.net : b.price[0]?.price?.net)
            : this.convertStringToNumber(b.price.length>1 ? b.price[1]?.price?.gross : b.price[0]?.price?.gross);

            return (currentPriceB) - (currentPriceA);
          });
        } else {
          definedPrices.sort((a, b) => {
            return this.convertStringToNumber(b.price[0]?.price?.gross) - this.convertStringToNumber(a.price[0]?.price?.gross);
          });
        }

        // On concatène les produits avec prix défini et les produits avec prix non défini
        this.sortedProducts = definedPrices.concat(undefinedPrices);
        break;
      case 'priceKgAsc':
        // On compare les prix par poids/litre en fonction du type de prix sélectionné : prix net ou prix brut
        // Tri Ascendant
        definedPrices.sort((a, b) => {
          let currentPriceA = isNetPrice
            ? this.convertStringToNumber(a.price.length>1 ? a.price[1]?.price?.netByWeight : a.price[0]?.price?.netByWeight)
            : this.convertStringToNumber(a.price.length>1 ? a.price[1]?.price?.grossByWeight : a.price[0]?.price?.grossByWeight);
            let currentPriceB = isNetPrice
            ? this.convertStringToNumber(b.price.length>1 ? b.price[1]?.price?.netByWeight : b.price[0]?.price?.netByWeight)
            : this.convertStringToNumber(b.price.length>1 ? b.price[1]?.price?.grossByWeight : b.price[0]?.price?.grossByWeight);

          return (currentPriceA) - (currentPriceB);
        });

        // On concatène les produits avec prix défini et les produits avec prix non défini
        this.sortedProducts = definedPrices.concat(undefinedPrices);
        break;
      case 'priceKgDesc':
        // On compare les prix par poids/litre en fonction du type de prix sélectionné : prix net ou prix brut
        // Tri Ascendant
        definedPrices.sort((a, b) => {
          let currentPriceA = isNetPrice
          ? this.convertStringToNumber(a.price.length>1 ? a.price[1]?.price?.netByWeight : a.price[0]?.price?.netByWeight)
          : this.convertStringToNumber(a.price.length>1 ? a.price[1]?.price?.grossByWeight : a.price[0]?.price?.grossByWeight);
          let currentPriceB = isNetPrice
          ? this.convertStringToNumber(b.price.length>1 ? b.price[1]?.price?.netByWeight : b.price[0]?.price?.netByWeight)
          : this.convertStringToNumber(b.price.length>1 ? b.price[1]?.price?.grossByWeight : b.price[0]?.price?.grossByWeight);

          return (currentPriceB) - (currentPriceA);
        });

        // On concatène les produits avec prix défini et les produits avec prix non défini
        this.sortedProducts = definedPrices.concat(undefinedPrices);
        break;
      case 'markAsc':
        // On compare par ordre alphabétique ascendant sur les marques
        definedMark.sort((a, b) => {
          return a.brand?.localeCompare(b.brand);
        });

        this.sortedProducts = definedMark.concat(undefinedMark);
        break;
      case 'markDesc':
        // On compare par ordre alphabétique déscendant sur les marques
        definedMark.sort((a, b) => {
          return b.brand?.localeCompare(a.brand);
        });

        this.sortedProducts = definedMark.concat(undefinedMark);
        break;
      case 'designationAsc':
        // On compare par ordre alphabétique ascendant sur les désignations
        this.sortedProducts.sort((a, b) => {
          return a.label.localeCompare(b.label);
        });
        break;
      case 'designationDesc':
        // On compare par ordre alphabétique déscendant sur les désignations
        this.sortedProducts.sort((a, b) => {
          return b.label.localeCompare(a.label);
        });
        break;
      default:
        if (this.sortedProductsSave)
          this.sortedProducts = JSON.parse(JSON.stringify(this.sortedProductsSave));
        break;
    }
    return this.sortedProducts;
  }

  public clearSort() {
    if (this.sortedProductsSave)
      this.sortedProducts = JSON.parse(JSON.stringify(this.sortedProductsSave));
    this.typeSort = null;
  }

  public setGrouping(isGroupingEnable: boolean) {
    this.isGroupingEnable = isGroupingEnable;
  }
}



@UntilDestroy()
export class DataTableSort {
  sorts: any;
  sortDir: string;
  column: any;
  prevValue: any;
  newValue: any;
}
